/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Box, Button, Lightbox, Table } from '@nexigendigital/nxui'
import { Addon } from 'models/shop'
import { useCheckoutCartMutation, useCreateCartMutation, useDeleteCartMutation, usePaymentMethodsQuery } from 'api/shop'
import { useAccountPaymentMethodsQuery } from 'api/billing'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    addon: Partial<Addon>,
    status: boolean,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function AddonPurchaseLightbox({ addon, status, close }: Props) {
    let conditionalProps
    const [template, setTemplate] = useState('configuration')
    const [, { data: cart }] = useCreateCartMutation({ fixedCacheKey: 'addon-purchase-cart' })
    const { data: paymentMethods } = usePaymentMethodsQuery(undefined)
    const { data: accountPaymentMethods } = useAccountPaymentMethodsQuery(undefined)
    const [checkoutCart, { isSuccess: isCheckoutCartSuccess, isLoading: isCheckingOutCart, data: checkout }] = useCheckoutCartMutation()
    const [deleteCart] = useDeleteCartMutation()
    const columns = useMemo(() => ([
        {
            Header: 'Addon',
            accessor: 'addon'
        },
        {
            Header: 'Quantity',
            accessor: 'quantity'
        },
        {
            Header: 'Price',
            accessor: 'price'
        }
    ]), [])
    const data = useMemo(() => ([{
        addon: addon.name,
        quantity: addon.quantity,
        price: addon?.price?.total
    }]), [addon])

    switch (template) {
        case 'confirmation':
            conditionalProps = {
                title: 'Confirm Addon',
                type: 'confirm',
                icon: 'info',
                description: 'When you confirm this submission, an invoice for your addon will be generated. This invoice must be paid in full in order for the addon to take affect.',
                loading: isCheckingOutCart,
                conditions: { state: status, action: () => close() },
                actions: [
                    {
                        label: 'No, return to Services',
                        color: 'error',
                        type: 'button',
                        func: () => {
                            if (cart) {
                                deleteCart(cart.uuid)
                            }

                            close()
                        }
                    },
                    {
                        label: 'Generate Invoice',
                        color: 'primary',
                        disabled: isCheckingOutCart,
                        type: 'button',
                        func: () => {
                            if (cart && paymentMethods && accountPaymentMethods) {
                                const creditCard = paymentMethods.find(({ name }: { name: string }) => name === 'Credit Card')
                                const defaultPayment = accountPaymentMethods.find(({ is_default }) => is_default)

                                checkoutCart({
                                    uuid: cart.uuid,
                                    payment_method_id: creditCard?.id,
                                    payment_method_data_id: defaultPayment?.id
                                })
                            }
                        }
                    }
                ]
            }
            break
        case 'configuration':
        default:
            conditionalProps = {
                title: 'Addon',
                type: 'custom',
                content: (
                    <BoxWrapper
                        header={{ title: 'Addon Configuration' }}
                        footer={(
                            <Footer>
                                <Button color={'flat'} type={'button'}>Cancel</Button>
                                <Button
                                    color={'primary'}
                                    type={'button'}
                                    onClick={() => setTemplate('confirmation')}
                                >
                                    Configure
                                </Button>
                            </Footer>
                        )}
                    >
                        <Table
                            render={{
                                columns: () => columns,
                                data: () => data
                            }}
                            conditions={{
                                sort: false,
                                pagination: false
                            }}
                        />
                    </BoxWrapper>
                ),
                conditions: { state: status, action: () => close() }
            }
    }

    useEffect(() => {
        if (isCheckoutCartSuccess && checkout) {
            console.log(checkout)
            // dispatch(setInvoice(data.id))
        }
    }, [data, isCheckoutCartSuccess])

    return <Lightbox {...conditionalProps} />
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const BoxWrapper = styled(Box)`
    margin-bottom: 0;

    table {
        tr {
            border: none;
        }
    }
`

const Footer = styled.footer`
    display: flex;
    justify-content: flex-end;
    padding: 12px 24px;
    gap: 12px;
    background-color: ${props => (props.theme['background--100'])};
`
